import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Typography, Box, Container, Paper } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // You can log the error to an error reporting service
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({ errorInfo });

    // Could send to an error reporting service here
    // reportError(error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: { xs: 3, sm: 4 },
              textAlign: 'center',
              borderRadius: 0,
              border: '2px solid currentColor',
              background: 'transparent',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 72,
                height: 72,
                mb: 2,
                border: '2px solid currentColor',
                borderRadius: 0,
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 40, color: '#a02018' }} />
            </Box>
            <Typography
              variant="h4"
              gutterBottom
              component="div"
              sx={{
                fontFamily: '"VT323", monospace',
                letterSpacing: 3,
                color: '#a02018',
                textTransform: 'uppercase',
              }}
            >
              [ POST MORTEM ]
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mb: 1, fontFamily: '"VT323", monospace', fontSize: '1.1rem' }}
            >
              Something broke, but your time wallet is safe.
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 3, fontFamily: '"VT323", monospace', fontSize: '0.95rem' }}
            >
              Everything on wipe.haus expires eventually — this crash was just a little early.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={this.handleReload}>
                Try again
              </Button>
              <Button variant="outlined" onClick={this.handleGoHome}>
                Back to home
              </Button>
            </Box>
            {process.env.NODE_ENV !== 'production' && this.state.error && (
              <Box
                sx={{
                  mt: 4,
                  textAlign: 'left',
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontFamily: 'monospace', mb: 1 }}
                >
                  Error: {this.state.error.toString()}
                </Typography>
                {this.state.errorInfo && (
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxHeight: '200px',
                      overflow: 'auto',
                    }}
                  >
                    {this.state.errorInfo.componentStack}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
